import { Box, Grid, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import PropTypes from "prop-types";
import React from "react";
import FloorPlanCard from "./FloorPlanCard";
import { FloorPlanGridSkeleton } from "./FloorPlanLibrarySkeleton";

const skeletonRegionSx = (libraryFillsParentHeight) => ({
  width: "100%",
  ...(libraryFillsParentHeight
    ? {
        flex: 1,
        minHeight: 0,
        maxHeight: "100%",
      }
    : {
        // Match the loaded grid wrapper (`hasPlans` inner Box) so skeleton tiles
        // swap in place without a viewport-height jump when data arrives.
        flex: 1,
        minHeight: 0,
        height: "100%",
      }),
});

/**
 * `/main` bento library: floor-plan grid (md+ only).
 *
 * Below the `md` breakpoint this component renders nothing — the mobile floor
 * plan UI is owned by `MobileFloorPlanSlider`, which is rendered as a top-level
 * sibling in `UserMainHome` so it can ignore the Paper/Container insets that
 * wrap this body.
 */
function MainBentoCombinedLibraryBody({
  layoutsFetching,
  libraryFillsParentHeight,
  /** Pre-filtered plan items (same shape as nodes from `layoutData` / `filterLayouts`). */
  floorPlanItems,
  /** Dynamic heading from `/main` (folder name + count). */
  sectionTitle = "Floor plans",
  navigate,
  setActiveLayout,
  setShowVersionLayout,
  setEditVersionLayout,
  clickTimeout,
  setClickTimeout,
  setOptionsMenu,
  moreOptionsMenu,
  setEditableLayoutId,
}) {
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const handleCardClick = (item) => {
    if (clickTimeout) clearTimeout(clickTimeout);
    const timeout = setTimeout(() => {
      if (item?.version == 2 && item?.payload?.roomsDataCount == 0) {
        setActiveLayout(item);
        setEditVersionLayout(2);
        navigate(`/floor-plan/${item?.uuid}/version/${2}`);
      } else {
        setShowVersionLayout(item?._id);
        navigate(`/floor-gallery/${item?._id}`);
      }
    }, 200);
    setClickTimeout(timeout);
  };

  const handleCardDoubleClick = (item) => {
    if (clickTimeout) clearTimeout(clickTimeout);
    setActiveLayout({
      name: item?.name,
      uuid: item?.uuid,
    });
  };

  const handleMoreOptionsClick = (item) => {
    setOptionsMenu(moreOptionsMenu);
    setEditableLayoutId(item);
  };

  const hasPlans = (floorPlanItems?.length ?? 0) >= 1;

  const emptyState = (
    <Box
      sx={{
        width: "100%",
        flex: libraryFillsParentHeight ? 1 : undefined,
        minHeight: libraryFillsParentHeight ? 0 : { xs: 200, sm: 240 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: libraryFillsParentHeight ? 2 : 3,
        boxSizing: "border-box",
      }}
    >
      <Typography color="text.secondary" sx={{ textAlign: "center" }}>
        No floor plans yet.
      </Typography>
    </Box>
  );

  const titleTypography = (
    <Typography
      component="h2"
      sx={{
        fontSize: "1.25rem",
        fontWeight: 600,
        letterSpacing: "-0.02em",
        color: "text.primary",
        textAlign: "left",
        pt: 2,
        pl: 1,
        pr: 0,
        mb: 2,
        flexShrink: 0,
        position: "relative",
        top: "4px",
      }}
    >
      {sectionTitle}
    </Typography>
  );

  if (!isMdUp) {
    return null;
  }

  if (layoutsFetching) {
    return (
      <Box
        aria-busy="true"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          ...(libraryFillsParentHeight
            ? { flex: 1, minHeight: 0, height: "100%" }
            : {}),
        }}
      >
        {titleTypography}
        <Box sx={skeletonRegionSx(libraryFillsParentHeight)}>
          <FloorPlanGridSkeleton />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        ...(libraryFillsParentHeight
          ? { flex: 1, minHeight: 0, height: "100%" }
          : {}),
      }}
    >
      {titleTypography}
      {hasPlans ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Grid container spacing={2} alignItems="stretch" mt={0}>
            {floorPlanItems.map((item, idx) => (
              <Grid
                key={item?._id ?? idx}
                item
                md={6}
                sx={{ display: "flex", minWidth: 0 }}
              >
                <FloorPlanCard
                  item={item}
                  onClick={() => handleCardClick(item)}
                  onDoubleClick={() => handleCardDoubleClick(item)}
                  onMoreOptionsClick={() => handleMoreOptionsClick(item)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        emptyState
      )}
    </Box>
  );
}

MainBentoCombinedLibraryBody.propTypes = {
  layoutsFetching: PropTypes.bool,
  libraryFillsParentHeight: PropTypes.bool,
  floorPlanItems: PropTypes.array,
  sectionTitle: PropTypes.string,
  navigate: PropTypes.func.isRequired,
  setActiveLayout: PropTypes.func.isRequired,
  setShowVersionLayout: PropTypes.func.isRequired,
  setEditVersionLayout: PropTypes.func.isRequired,
  clickTimeout: PropTypes.object,
  setClickTimeout: PropTypes.func.isRequired,
  setOptionsMenu: PropTypes.func.isRequired,
  moreOptionsMenu: PropTypes.array,
  setEditableLayoutId: PropTypes.func.isRequired,
  editableLayoutId: PropTypes.object,
  bentoDraggingPlanId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]),
  onBentoPlanDragStart: PropTypes.func,
  onBentoPlanDragEnd: PropTypes.func,
};

export default MainBentoCombinedLibraryBody;
